import Popup from 'devextreme-react/popup';
import type { ToolbarItem } from 'devextreme/ui/popup';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { useOpsGridPopupSize } from './useOpsGridPopupSize';
import './opsDataGrid.css';

export interface GridFormPopupProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  /** localStorage key for persisted popup size (use opsGridFormPopupSizeKey(gridStateStorageKey)). */
  sizeStorageKey: string;
  /** When content exceeds this, the body scrolls (popup still caps at 90vh). */
  maxBodyHeight?: number;
  /** Optional first-open desktop height. User-resized height still wins once persisted. */
  preferredHeight?: number;
  /** Optional minimum desktop width. Persisted sizes are clamped to this floor. */
  minWidth?: number;
  /** Optional minimum desktop height. Useful for popups with fixed-height interactive panels. */
  minHeight?: number;
  /** Extra class on the popup root (e.g. opt-in flex layouts). */
  bodyClassName?: string;
  error?: string | null;
  /** Extra items on the popup top toolbar, rendered before the close button. */
  toolbarItems?: ToolbarItem[];
  /** Restore + maximize in the title bar (desktop). Disables drag so the dialog stays centered. */
  showSizeChrome?: boolean;
  children: ReactNode;
  footer: ReactNode;
}

/** Resizable edit/create popup shell for site grid pages (scroll body + pinned footer). */
export function GridFormPopup({
  visible,
  title,
  onClose,
  width = 620,
  sizeStorageKey,
  maxBodyHeight = 680,
  preferredHeight,
  minWidth,
  minHeight,
  bodyClassName,
  error,
  toolbarItems,
  showSizeChrome = false,
  children,
  footer
}: GridFormPopupProps) {
  const {
    width: popupWidth,
    height: popupHeight,
    hasUserSize,
    isMaximized,
    maximize,
    restoreDefault,
    onResizeEnd
  } = useOpsGridPopupSize(width, sizeStorageKey, { minWidth, minHeight });
  const requestedHeight = hasUserSize ? popupHeight : preferredHeight;
  const effectiveHeight =
    requestedHeight != null && minHeight != null
      ? Math.max(requestedHeight, minHeight)
      : requestedHeight ?? minHeight;
  const effectiveWidth =
    minWidth != null ? Math.max(popupWidth, minWidth) : popupWidth;
  const popupOptions = useResponsivePopupOptions({
    width: effectiveWidth,
    height: effectiveHeight
  });
  const isDesktopPopup = !popupOptions.fullScreen;
  const useFixedSize = isDesktopPopup && effectiveHeight != null;
  const mergedToolbarItems = useMemo((): ToolbarItem[] | undefined => {
    const extras = toolbarItems ?? [];
    if (!showSizeChrome || !isDesktopPopup) return extras.length > 0 ? extras : undefined;
    return [
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'revert',
          hint: 'Restore default size',
          stylingMode: 'text',
          onClick: restoreDefault
        }
      },
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'fullscreen',
          hint: isMaximized ? 'Already maximized' : 'Maximize',
          stylingMode: 'text',
          disabled: isMaximized,
          onClick: maximize
        }
      },
      ...extras
    ];
  }, [isDesktopPopup, isMaximized, maximize, restoreDefault, showSizeChrome, toolbarItems]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      title={title}
      showCloseButton
      toolbarItems={mergedToolbarItems}
      dragEnabled={isDesktopPopup && !showSizeChrome}
      resizeEnabled={isDesktopPopup && !(showSizeChrome && isMaximized)}
      deferRendering={false}
      animation={{ show: { duration: 0 }, hide: { duration: 0 } }}
      shading
      height={popupOptions.fullScreen ? '100%' : useFixedSize ? effectiveHeight : 'auto'}
      maxHeight={popupOptions.fullScreen ? undefined : '90vh'}
      width={isDesktopPopup ? effectiveWidth : undefined}
      maxWidth={isDesktopPopup ? '90vw' : undefined}
      fullScreen={popupOptions.fullScreen}
      onResizeEnd={isDesktopPopup ? onResizeEnd : undefined}
      position={{ my: 'center', at: 'center', of: window }}
      wrapperAttr={{
        class: `ops-grid-detail-popup-wrapper${useFixedSize ? ' ops-grid-detail-popup-wrapper--sized' : ''}`
      }}
    >
      <div
        className={`ops-grid-detail-popup${useFixedSize ? ' ops-grid-detail-popup--sized' : ''}${
          bodyClassName ? ` ${bodyClassName}` : ''
        }`}
        style={
          popupOptions.fullScreen
            ? undefined
            : ({ '--ops-grid-detail-body-max-height': `${maxBodyHeight}px` } as CSSProperties)
        }
      >
        {error ? (
          <div className="ops-grid-detail-popup__error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="ops-grid-detail-popup__body">
          <div className="ops-grid-detail-popup__content">{children}</div>
        </div>
        <div className="ops-grid-detail-popup__footer ops-grid-form-popup__footer">{footer}</div>
      </div>
    </Popup>
  );
}
